import { useState, useEffect, useMemo } from 'react';
import {
  X, Building2, GraduationCap, Layers, Search, Check, AlertCircle,
  RefreshCw, User, UsersRound, Filter, ChevronDown, ChevronUp, Calendar
} from 'lucide-react';
import { getAcademicYears } from '@/services/academicManagementService';
import { getCampusInfo, fetchStudentsForFeeAssignment, fetchFilieresGroupedForTuition, assignPaymentScheduleToStudents, type AlreadyScheduledStudent } from '@/services/tuitionService';
import type { PaymentScheduleTemplate, GroupedFiliereForTuition } from '@/types/tuition.types';
import { loadFamilyGroups } from './assignGroupFilters';

interface FilterOption {
  value: string;
  label: string;
  isCurrent?: boolean;
  code?: string;
  nombreAnnees?: number;
}

interface StudentOption {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

interface Props {
  schedule: PaymentScheduleTemplate;
  onClose: () => void;
  onSuccess: () => void;
}

const scheduleTypeLabels: Record<string, string> = {
  ANNUAL: 'Annuel', SEMESTRIAL: 'Semestriel', TRIMESTRIAL: 'Trimestriel', MONTHLY: 'Mensuel', CUSTOM: 'Personnalisé',
};

const PaymentScheduleAssignModal: React.FC<Props> = ({ schedule, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Students found to already have a schedule → shown in a switch-confirm dialog
  const [switchConfirm, setSwitchConfirm] = useState<AlreadyScheduledStudent[] | null>(null);

  // Data loading states
  const [academicYears, setAcademicYears] = useState<FilterOption[]>([]);
  const [campuses, setCampuses] = useState<FilterOption[]>([]);
  const [filieres, setFilieres] = useState<GroupedFiliereForTuition[]>([]);
  const [groups, setGroups] = useState<FilterOption[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Form states - cascading filters
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [selectedFiliere, setSelectedFiliere] = useState(''); // parent filière id
  const [selectedNiveau, setSelectedNiveau] = useState<number | ''>('');
  const [selectedOption, setSelectedOption] = useState(''); // option filière id
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [studentSearch, setStudentSearch] = useState('');

  // Get the selected (parent) filière's data for niveau + options dropdowns
  const selectedFiliereData = useMemo(() => {
    return filieres.find(f => f.id === selectedFiliere);
  }, [filieres, selectedFiliere]);

  const niveauOptions = useMemo(() => {
    if (!selectedFiliereData?.nombreAnnees) return [];
    return Array.from({ length: selectedFiliereData.nombreAnnees }, (_, i) => i + 1);
  }, [selectedFiliereData]);

  // Options available at the selected niveau (specialisation year). The Option
  // filter only appears once such a niveau is chosen.
  const availableOptions = useMemo(() => {
    if (!selectedFiliereData || !selectedNiveau) return [];
    return (selectedFiliereData.options || []).filter(
      o => o.isActive && (o.startNiveau ?? selectedFiliereData.nombreAnnees) <= selectedNiveau,
    );
  }, [selectedFiliereData, selectedNiveau]);

  // filière id sent to the backend: the option when picked, else the parent
  // (which the backend expands to itself + all its options).
  const effectiveFiliereId = selectedOption || selectedFiliere;

  // Filter students by search
  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return students;
    const search = studentSearch.toLowerCase();
    return students.filter(s =>
      s.firstName.toLowerCase().includes(search) ||
      s.lastName.toLowerCase().includes(search) ||
      s.studentCode.toLowerCase().includes(search)
    );
  }, [students, studentSearch]);

  // Load initial options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [years, campusInfo] = await Promise.all([
          getAcademicYears(),
          getCampusInfo(),
        ]);
        setAcademicYears(years);
        setCampuses(campusInfo.campuses.map(c => ({
          value: c.id,
          label: c.name,
        })));

        // Set default academic year to current
        const currentYear = years.find(y => y.isCurrent);
        if (currentYear) {
          setSelectedAcademicYear(currentYear.value);
        }

        // Load parent filières with their nested options.
        const fils = await fetchFilieresGroupedForTuition();
        setFilieres(fils);
      } catch (err) {
        console.error('Error loading options:', err);
        setError('Erreur lors du chargement des donnees');
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  // Reset filière-dependent selections when campus changes (students are
  // re-scoped to the campus below; the filière list is tenant-wide).
  useEffect(() => {
    setSelectedFiliere('');
    setSelectedNiveau('');
    setSelectedOption('');
    setSelectedGroup('');
    setSelectedStudents([]);
    setGroups([]);
  }, [selectedCampus]);

  // Reset the chosen option when filière or niveau changes.
  useEffect(() => {
    setSelectedOption('');
  }, [selectedFiliere, selectedNiveau]);

  // Load groups when filiere/niveau/academicYear/campus changes
  useEffect(() => {
    const loadGroups = async () => {
      if (!selectedFiliere || !selectedAcademicYear) {
        setGroups([]);
        setSelectedGroup('');
        return;
      }

      setIsLoadingGroups(true);
      setSelectedGroup('');

      try {
        const merged = await loadFamilyGroups({
          parentFiliereId: selectedFiliere,
          options: (selectedFiliereData?.options || []).map((o) => ({
            id: o.id,
            optionName: o.optionName,
            filiere: o.filiere,
          })),
          selectedOptionId: selectedOption,
          academicYearId: selectedAcademicYear,
          campusId: selectedCampus || undefined,
          niveau: selectedNiveau,
        });
        setGroups(merged);
      } catch (err) {
        console.error('Error loading groups:', err);
        setGroups([]);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    loadGroups();
  }, [selectedFiliere, selectedFiliereData, selectedOption, selectedNiveau, selectedAcademicYear, selectedCampus]);

  // Load students when filters change
  useEffect(() => {
    const loadStudents = async () => {
      if (!selectedAcademicYear) return;

      setIsLoadingStudents(true);
      setSelectedStudents([]);

      try {
        const data = await fetchStudentsForFeeAssignment({
          academicYearId: selectedAcademicYear,
          campusId: selectedCampus || undefined,
          filiereId: effectiveFiliereId || undefined,
          niveau: selectedNiveau || undefined,
          groupId: selectedGroup || undefined,
        });
        setStudents(data);
      } catch (err) {
        console.error('Error loading students:', err);
      } finally {
        setIsLoadingStudents(false);
      }
    };

    loadStudents();
  }, [selectedAcademicYear, selectedCampus, selectedFiliere, selectedOption, selectedNiveau, selectedGroup]);

  const handleAssign = async () => {
    if (!selectedAcademicYear) {
      setError('Veuillez selectionner une annee academique');
      return;
    }

    if (selectedStudents.length === 0) {
      setError('Veuillez selectionner au moins un etudiant');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Phase 1: assign to students without a schedule; the backend reports who
      // already has one so we can ask for an explicit switch confirmation.
      const result = await assignPaymentScheduleToStudents({
        templateId: schedule.id,
        studentIds: selectedStudents,
        academicYearId: selectedAcademicYear,
        replaceExisting: false,
      });

      const already = result.alreadyScheduled ?? [];
      if (already.length > 0) {
        // Keep the freshly-assigned ones out of the way; ask about the rest.
        setSwitchConfirm(already);
        if (result.assignedCount > 0) {
          setSuccess(`${result.assignedCount} étudiant(s) assigné(s). ${already.length} ont déjà un échéancier — confirmez le changement ci-dessous.`);
        }
        return;
      }

      finishWithResult(result.assignedCount, 0);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const finishWithResult = (assigned: number, replaced: number) => {
    const parts: string[] = [];
    if (assigned > 0) parts.push(`${assigned} assigné(s)`);
    if (replaced > 0) parts.push(`${replaced} changé(s)`);
    setSuccess(`Échéancier « ${schedule.name} » : ${parts.join(', ') || 'aucun changement'}`);
    setSelectedStudents([]);
    setTimeout(() => onSuccess(), 1600);
  };

  // Phase 2: the user confirmed switching the already-scheduled students.
  const confirmSwitch = async () => {
    if (!switchConfirm) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await assignPaymentScheduleToStudents({
        templateId: schedule.id,
        studentIds: switchConfirm.map((s) => s.studentId),
        academicYearId: selectedAcademicYear,
        replaceExisting: true,
      });
      setSwitchConfirm(null);
      finishWithResult(0, result.replacedCount ?? switchConfirm.length);
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Assigner l'echeancier
              </h2>
              <p className="text-sm text-gray-500">
                {schedule.name} ({scheduleTypeLabels[schedule.type]} - {schedule.installments.length} echeances)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={18} />
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <Check size={18} />
              {success}
            </div>
          )}

          {isLoadingOptions ? (
            <div className="text-center py-8">
              <RefreshCw className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
              <p className="mt-2 text-gray-500">Chargement...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-900">Attribution d'echeancier</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      L'echeancier sera assigne aux etudiants selectionnes. Les echeances seront calculees
                      en fonction de la date de debut de l'annee academique.
                    </p>
                  </div>
                </div>
              </div>

              {/* Filters Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div className="flex items-center gap-2">
                    <Filter size={18} className="text-gray-500" />
                    <span className="font-medium text-gray-700">Filtres de selection</span>
                  </div>
                  {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {showFilters && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Academic Year */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annee academique <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={selectedAcademicYear}
                        onChange={(e) => setSelectedAcademicYear(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Selectionner</option>
                        {academicYears.map((year) => (
                          <option key={year.value} value={year.value}>
                            {year.label} {year.isCurrent ? '(En cours)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Campus */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Building2 size={14} className="inline mr-1" />
                        Campus (optionnel)
                      </label>
                      <select
                        value={selectedCampus}
                        onChange={(e) => setSelectedCampus(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="">Tous les campus</option>
                        {campuses.map((campus) => (
                          <option key={campus.value} value={campus.value}>
                            {campus.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Filiere */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <GraduationCap size={14} className="inline mr-1" />
                        Filiere (optionnel)
                      </label>
                      <select
                        value={selectedFiliere}
                        onChange={(e) => setSelectedFiliere(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                      >
                        <option value="">Toutes les filieres</option>
                        {filieres.map((fil) => (
                          <option key={fil.id} value={fil.id}>
                            {fil.filiere}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Niveau */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Layers size={14} className="inline mr-1" />
                        Niveau (optionnel)
                      </label>
                      <select
                        value={selectedNiveau}
                        onChange={(e) => setSelectedNiveau(e.target.value ? parseInt(e.target.value) : '')}
                        disabled={!selectedFiliere || niveauOptions.length === 0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                      >
                        <option value="">Tous les niveaux</option>
                        {niveauOptions.map((n) => (
                          <option key={n} value={n}>
                            {n === 1 ? '1ere annee' : `${n}eme annee`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Option — only shown once a niveau with options is selected.
                        "Toutes les options" affects every option student of the
                        parent at this niveau. */}
                    {availableOptions.length > 0 && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <GraduationCap size={14} className="inline mr-1" />
                          Option (optionnel)
                        </label>
                        <select
                          value={selectedOption}
                          onChange={(e) => setSelectedOption(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Toutes les options</option>
                          {availableOptions.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.optionName || opt.filiere}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Group */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <UsersRound size={14} className="inline mr-1" />
                        Groupe (optionnel)
                      </label>
                      <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        disabled={!selectedFiliere || isLoadingGroups}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100"
                      >
                        <option value="">Tous les groupes</option>
                        {groups.map((grp) => (
                          <option key={grp.value} value={grp.value}>
                            {grp.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Students Selection */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <User size={18} className="text-gray-500" />
                      <span className="font-medium text-gray-700">
                        Selection des etudiants
                      </span>
                      {students.length > 0 && (
                        <span className="text-sm text-gray-500">
                          ({students.length} etudiant{students.length > 1 ? 's' : ''} trouve{students.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                    {students.length > 0 && (
                      <button
                        onClick={selectAllStudents}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {selectedStudents.length === filteredStudents.length ? 'Deselectionner tout' : 'Tout selectionner'}
                      </button>
                    )}
                  </div>
                  {students.length > 0 && (
                    <div className="mt-2 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        placeholder="Rechercher un etudiant..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  )}
                </div>

                <div className="max-h-64 overflow-y-auto">
                  {isLoadingStudents ? (
                    <div className="p-8 text-center">
                      <RefreshCw className="mx-auto h-6 w-6 text-gray-400 animate-spin" />
                      <p className="mt-2 text-sm text-gray-500">Chargement des etudiants...</p>
                    </div>
                  ) : filteredStudents.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {filteredStudents.map((student) => (
                        <label
                          key={student.id}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStudents.includes(student.id)}
                            onChange={() => toggleStudentSelection(student.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {student.photoUrl ? (
                              <img src={student.photoUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <User size={16} className="text-gray-500" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {student.firstName} {student.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{student.studentCode}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-500">
                      <User className="mx-auto h-8 w-8 text-gray-300" />
                      <p className="mt-2 text-sm">
                        {!selectedAcademicYear
                          ? 'Selectionnez une annee academique'
                          : 'Aucun etudiant trouve avec ces criteres'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment Preview */}
              {selectedStudents.length > 0 && (
                <div className="bg-primary-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-primary-900">Affectation prevue</p>
                      <p className="text-sm text-primary-700">
                        {selectedStudents.length} etudiant(s) selectionne(s)
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={isSubmitting || !selectedAcademicYear || selectedStudents.length === 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Assignation...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Assigner ({selectedStudents.length})
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Switch confirmation: students already on a plan */}
      {switchConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <RefreshCw className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Changer d'échéancier ?</h3>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <p className="text-sm text-gray-700 mb-3">
                {switchConfirm.length} étudiant(s) ont déjà un échéancier assigné. Voulez-vous les
                faire passer à <strong>« {schedule.name} »</strong> ?
              </p>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                {switchConfirm.map((s) => (
                  <div key={s.studentId} className="px-3 py-2 flex items-center justify-between text-sm">
                    <span className="text-gray-800">{s.name}</span>
                    <span className="text-gray-500">
                      Actuel : <span className="font-medium">{s.currentScheduleName || 'Échéancier'}</span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Les échéances seront recalculées selon le nouveau plan. Les paiements déjà
                enregistrés sont conservés et réappliqués sur les nouvelles échéances (les plus
                anciennes d'abord).
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setSwitchConfirm(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmSwitch}
                disabled={isSubmitting}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isSubmitting && <RefreshCw size={16} className="animate-spin" />}
                Confirmer le changement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentScheduleAssignModal;
